import React from "react";
import { useTranslation } from "react-i18next";

import { Dropdown, DropdownItem, Spinner } from "flowbite-react";
import { Button } from "../Button";

/**
 * CommonExportProps - Props for the CommonExport component.
 */
type TCommonExportProps = {
  isCSV: boolean;
  isXLS: boolean;
  isXLSX: boolean;
  isLoading?: boolean;
  onFormatSelect: (format: string) => void;
};

/**
 * CommonExport - CommonExport component for exporting data using shared Button and DropdownMenu.
 */
const CommonExport: React.FC<TCommonExportProps> = ({
  isCSV,
  isXLS,
  isXLSX,
  isLoading = false,
  onFormatSelect,
}) => {
  const { t } = useTranslation(["common"]);
  // const [isOpen, setIsOpen] = React.useState(false);

  // Filter available formats
  const availableFormats = [
    { key: "csv", label: "CSV", enabled: isCSV },
    { key: "xls", label: "XLS", enabled: isXLS },
    { key: "xlsx", label: "XLSX", enabled: isXLSX },
  ].filter((format) => format.enabled);

  const handleFormatSelect = (format: string) => {
    if (isLoading) {
      return;
    }
    onFormatSelect(format);
    // setIsOpen(false);
  };

  return (
    <>
      <Dropdown
        size="sm"
        label=""
        dismissOnClick={false}
        renderTrigger={() => (
          <Button
            variant="outline"
            size="sm"
            className="group flex items-center gap-2 bg-background"
            disabled={isLoading || availableFormats.length === 0}
            data-testid="exportSelectTestId"
          >
            <span className="flex items-center">
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              {t("EXPORT.BUTTON")}
            </span>

            {isLoading && <Spinner size="sm" />}
          </Button>
        )}
      >
        {availableFormats.length > 0 ? (
          availableFormats.map((format) => (
            <DropdownItem
              key={format.key}
              onClick={() => handleFormatSelect(format.key)}
              className="flex items-center"
              data-testid={`${format.key}ExportTestId`}
            >
              {format.label}
            </DropdownItem>
          ))
        ) : (
          <div className="px-4 py-2 text-sm text-gray">
            {t("EXPORT.NO_FORMATS")}
          </div>
        )}
      </Dropdown>
    </>
  );
};

export default React.memo(CommonExport);
